
// VendorCare.js
import React, { useState, useEffect, useMemo } from "react";
import "./VendorCare.css";
import { Link } from "react-router-dom";
import {
    getAllCustomerTicketsAPI,
    updateCustomerTicketStatusAPI,
    getCustomerTicketByIdAPI,
} from "../../../Servicefile/SupportManagementService.jsx";

const STATUS_OPTIONS = ["OPEN", "IN_PROGRESS", "RESOLVED", "REJECTED"];
// const ADMIN_RESPONSE_OPTIONS = [
//     "PENDING_REVIEW",
//     "UNDER_INVESTIGATION",
//     "RESOLVED_REFUNDED",
//     "RESOLVED_REPLACED",
//     "RESOLVED_COMPENSATED",
//     "REJECTED_INVALID",
//     "REJECTED_POLICY",
//     "ESCALATED"
// ];
const getStatusBadgeClass = (status) => {
    switch (status) {
        case "OPEN":
            return "status-open";
        case "IN_PROGRESS":
            return "status-progress";
        case "RESOLVED":
            return "status-resolved";
        case "REJECTED":
            return "status-rejected";
        default:
            return "status-default";
    }
};
const useInlineLoader = () => {
    const [loading, setLoading] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);

    const showLoader = () => {
        setFadeOut(false);
        setLoading(true);
    };

    const hideLoader = () => {
        setFadeOut(true);
        setTimeout(() => {
            setLoading(false);
            setFadeOut(false);
        }, 500);
    };

    return { showLoader, hideLoader, loading, fadeOut };
};

const Loader = ({ loading, fadeOut, text = "Loading..." }) => {
    if (!loading) return null;

    return (
        <div className="global-loader-overlay" style={{
            opacity: fadeOut ? 0 : 1,
            pointerEvents: fadeOut ? 'none' : 'auto'
        }}>
            <div className="loader-content">
                <div className="loader-brand">
                    {"MAAMAAS".split("").map((l, i) => (
                        <span key={i} className="loader-letter" style={{ animationDelay: `${i * 0.1}s` }}>
                            {l}
                        </span>
                    ))}
                </div>
                <div className="loader-progress-bar">
                    <div className="loader-progress-fill"></div>
                </div>
                <div className="loader-text">{text}</div>
            </div>
        </div>
    );
};

const Usercoustomercare = () => {
    const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
    const [selectedTicketTypeFilter, setSelectedTicketTypeFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showViewModal, setShowViewModal] = useState(false);
    const [userStats, setUserStats] = useState({});
    const [currentIssueForView, setCurrentIssueForView] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [viewLoading, setViewLoading] = useState(false);
    const [newStatus, setNewStatus] = useState("");
    const [adminResponse, setAdminResponse] = useState("");
    const [additionalNotes, setAdditionalNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [localAdminResponses, setLocalAdminResponses] = useState({});
    const [imagePreviewModal, setImagePreviewModal] = useState({ show: false, url: "" });

    const { showLoader, hideLoader, loading: globalLoading, fadeOut } = useInlineLoader();

    useEffect(() => {
        if (showViewModal || imagePreviewModal.show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => { document.body.style.overflow = 'auto'; };
    }, [showViewModal, imagePreviewModal.show]);

    const handleStatusChange = async (ticketId, statusToSet, adminResponseToInclude = null) => {
        try {
            const data = await updateCustomerTicketStatusAPI(ticketId, statusToSet, adminResponseToInclude);
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    const storeAdminResponseLocally = (ticketId, response, notes = "") => {
        setLocalAdminResponses(prev => ({
            ...prev,
            [ticketId]: {
                response,
                notes,
                timestamp: new Date().toISOString(),
                adminName: localStorage.getItem("userName") || "Admin"
            }
        }));
    };

    const getCombinedAdminResponse = (ticket) => {
        const id = ticket.ticketId || ticket.id;
        const localResponse = localAdminResponses[id];
        if (localResponse) {
            return {
                response: localResponse.response,
                notes: localResponse.notes,
                isLocal: true,
                timestamp: localResponse.timestamp,
            };
        }
        return {
            response: ticket.adminResponse,
            notes: "",
            isLocal: false,
            timestamp: ticket.resolvedAt || ticket.createdAt
        };
    };

    useEffect(() => {
        fetchTickets();
        const savedResponses = localStorage.getItem("localAdminResponses");
        if (savedResponses) {
            try { setLocalAdminResponses(JSON.parse(savedResponses)); } catch (_) { }
        }
    }, []);

    useEffect(() => {
        localStorage.setItem("localAdminResponses", JSON.stringify(localAdminResponses));
    }, [localAdminResponses]);

    const calculateStats = (ticketList) => {
        const counts = { OPEN: 0, IN_PROGRESS: 0, RESOLVED: 0, REJECTED: 0, Total: 0 };
        ticketList.forEach(ticket => {
            const status = ticket.status || "UNKNOWN";
            if (STATUS_OPTIONS.includes(status)) counts[status]++;
        });
        counts.Total = ticketList.length;
        setUserStats(counts);
    };

    const fetchTickets = async () => {
        setLoading(true);
        setError("");
        showLoader();

        try {
            const customerTickets = await getAllCustomerTicketsAPI();
            setTickets(customerTickets);
            calculateStats(customerTickets);
        } catch (err) {
            setError(err.message || "Something went wrong");
        } finally {
            setLoading(false);
            hideLoader();
        }
    };

    const handleShowViewModal = async (ticket) => {
        setShowViewModal(true);
        setViewLoading(true);
        setAdminResponse("");
        setNewStatus("");
        setAdditionalNotes("");

        const ticketId = ticket.ticketId || ticket.id;

        try {
            const ticketData = await getCustomerTicketByIdAPI(ticketId);
            setCurrentIssueForView(ticketData);
        } catch (error) {
            console.error("Error fetching ticket details:", error);
            setCurrentIssueForView({
                ...ticket,
                id: ticket.id || ticket.ticketId,
                ticketId: ticket.ticketId || ticket.id,
                description: ticket.description || ticket.message || "No description provided",
            });
        } finally {
            setViewLoading(false);
        }
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setCurrentIssueForView(null);
        setAdminResponse("");
        setNewStatus("");
        setAdditionalNotes("");
        setSaving(false);
    };

    const showImagePreview = (url) => setImagePreviewModal({ show: true, url });
    const closeImagePreview = () => setImagePreviewModal({ show: false, url: "" });

    const filteredTickets = useMemo(() => {
        return tickets.filter(ticket => {
            const matchesStatus = !selectedStatusFilter || ticket.status === selectedStatusFilter;
            const type = ticket.issueType || ticket.ticketType;
            const matchesType = !selectedTicketTypeFilter || type === selectedTicketTypeFilter;
            const desc = ticket.description || ticket.message || "";
            const matchesSearch = !searchTerm ||
                desc.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (ticket.id && ticket.id.toString().includes(searchTerm)) ||
                (ticket.ticketId && ticket.ticketId.toString().includes(searchTerm)) ||
                (ticket.userId && ticket.userId.toString().includes(searchTerm)) ||
                (ticket.orderId && ticket.orderId.toString().includes(searchTerm));

            return matchesStatus && matchesType && matchesSearch;
        }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [tickets, selectedStatusFilter, selectedTicketTypeFilter, searchTerm]);

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case "OPEN": return "status-open";
            case "IN_PROGRESS": return "status-progress";
            case "RESOLVED": return "status-resolved";
            case "REJECTED": return "status-rejected";
            default: return "status-default";
        }
    };

    const getTicketTypeBadgeClass = (ticketType) => {
        switch (ticketType) {
            case "SERVICE_QUALITY": return "type-service";
            case "FOOD_QUALITY": return "type-food";
            case "DELIVERY_ISSUE": return "type-delivery";
            case "PAYMENT_PROBLEM": return "type-payment";
            case "OTHER": return "type-other";
            default: return "type-default";
        }
    };

    // const getAdminResponseBadgeClass = (response) => {
    //     if (!response) return "response-default";
    //     if (response.includes("RESOLVED")) return "response-resolved";
    //     if (response.includes("REJECTED")) return "response-rejected";
    //     if (response.includes("PENDING") || response.includes("UNDER_INVESTIGATION")) return "response-pending";
    //     if (response === "ESCALATED") return "response-escalated";
    //     return "response-default";
    // };

    const formatTicketType = (ticketType) =>
        ticketType ? ticketType.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Unknown";

    // const formatAdminResponse = (response) => {
    //     if (!response) return "No Response";
    //     return response.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    // };

    const handleSaveChanges = async () => {
        if (!newStatus && !adminResponse) {
            alert("Please select a new status or admin response before saving.");
            return;
        }

        setSaving(true);

        try {
            const ticketId = currentIssueForView.ticketId || currentIssueForView.id;
            const statusToSet = newStatus || currentIssueForView.status;

            const result = await handleStatusChange(ticketId, statusToSet, adminResponse || null);

            if (!result.success) {
                alert("Update failed: " + result.error);
                setSaving(false);
                return;
            }

            if (adminResponse) {
                storeAdminResponseLocally(ticketId, adminResponse, additionalNotes);
            }

            setTickets(prev =>
                prev.map(ticket => {
                    const id = ticket.ticketId || ticket.id;
                    if (id === ticketId) {
                        return {
                            ...ticket,
                            status: statusToSet,
                            adminResponse: adminResponse || ticket.adminResponse,
                            resolvedAt: statusToSet === "RESOLVED" ? new Date().toISOString() : ticket.resolvedAt,
                        };
                    }
                    return ticket;
                })
            );

            setTickets(prev => {
                calculateStats(prev);
                return prev;
            });

            handleCloseViewModal();
            fetchTickets().catch(err => console.warn("Background refresh failed:", err));
            setTimeout(() => alert("Ticket updated successfully!"), 100);

        } catch (error) {
            console.error("Unexpected error:", error);
            alert("Something went wrong: " + error.message);
            setSaving(false);
        }
    };

    return (
        <div className="customer-care-container">
            <Loader loading={globalLoading} fadeOut={fadeOut} text="Loading tickets data..." />

            {/* <div className="customer-care-header">
                <h1 className="header-title">
                    <span className="header-icon">🎫</span>
                    Support Ticket Management
                </h1>
                <p className="header-subtitle">Manage and resolve customer support tickets efficiently</p>
            </div> */}

            {/* Stats Dashboard */}
            <div className="stats-dashboard">
                {STATUS_OPTIONS.map((status) => (
                    <div className="stat-card" key={`stat-${status}`}>
                        <div className={`stat-card-inner ${getStatusBadgeClass(status)}`}>
                            <div className="stat-number">{userStats[status] || 0}</div>
                            <div className="stat-label">{status}</div>
                        </div>
                    </div>
                ))}
                <div className="stat-card">
                    <div className="stat-card-inner stat-total">
                        <div className="stat-number">{userStats.Total || 0}</div>
                        <div className="stat-label">Total Tickets</div>
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            <div className="filters-section">
                <div className="filters-row">
                    <div className="filter-group">
                        <div className="search-wrapper">
                            <span className="search-icon">🔍</span>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search by ID, User, Order, or Message..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <select
                            className="filter-select"
                            value={selectedStatusFilter}
                            onChange={(e) => setSelectedStatusFilter(e.target.value)}
                        >
                            <option value="">All Status</option>
                            {STATUS_OPTIONS.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>

                        <select
                            className="filter-select"
                            value={selectedTicketTypeFilter}
                            onChange={(e) => setSelectedTicketTypeFilter(e.target.value)}
                        >
                            <option value="">All Types</option>
                            {["SERVICE_QUALITY", "FOOD_QUALITY", "DELIVERY_ISSUE", "PAYMENT_PROBLEM", "OTHER"].map(t => (
                                <option key={t} value={t}>{formatTicketType(t)}</option>
                            ))}
                        </select>

                        <button className="btn-clear-filters" onClick={() => {
                            setSelectedStatusFilter("");
                            setSelectedTicketTypeFilter("");
                            setSearchTerm("");
                        }}>
                            Clear All
                        </button>
                    </div>
                </div>
            </div>

            {/* Tickets Table */}
            <div className="table-wrapper">
                <div className="table-header-fixed">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: '6%' }}>Ticket</th>
                                <th style={{ width: '6%' }}>User</th>
                                <th style={{ width: '6%' }}>Order</th>
                                <th style={{ width: '8%' }}>Type</th>
                                <th style={{ width: '16%' }}>Message</th>
                                <th style={{ width: '7%' }}>Status</th>
                                <th style={{ width: '8%' }}>Date</th>
                                <th style={{ width: '7%' }}>Time</th>
                                <th style={{ width: '8%' }}>Resolved</th>
                                <th style={{ width: '12%' }}>Admin Response</th>
                                <th style={{ width: '6%' }}>Image</th>
                                <th style={{ width: '6%' }}>Actions</th>
                            </tr>
                        </thead>
                    </table>
                </div>
                <div className="table-body-scroll">
                    <table>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="12" className="loading-cell">
                                        <div className="table-loader">
                                            <div className="table-spinner"></div>
                                            <span>Loading tickets...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan="12" className="error-cell">
                                        <span className="error-icon">⚠️</span>
                                        {error}
                                    </td>
                                </tr>
                            ) : filteredTickets.length > 0 ? (
                                filteredTickets.map((ticket) => {
                                    const combinedResponse = getCombinedAdminResponse(ticket);
                                    const displayType = ticket.issueType || ticket.ticketType;
                                    const displayDesc = ticket.description || ticket.message || "No message";
                                    const displayId = ticket.ticketId || ticket.id;

                                    return (
                                        <tr key={displayId} className="ticket-row">
                                            <td style={{ width: '6%' }}>
                                                <span className="ticket-id">#{displayId}</span>
                                            </td>
                                            <td style={{ width: '6%' }}>
                                                {ticket.userId ? (
                                                    <Link to={`/user-profile/${ticket.userId}`} className="link-user">
                                                        {ticket.userId}
                                                    </Link>
                                                ) : "N/A"}
                                            </td>
                                            <td style={{ width: '6%' }}>
                                                {ticket.orderId ? (
                                                    <Link to={`/order-details/${ticket.orderId}`} className="link-order">
                                                        {ticket.orderId}
                                                    </Link>
                                                ) : "N/A"}
                                            </td>
                                            <td style={{ width: '8%' }}>
                                                <span className={`badge-type ${getTicketTypeBadgeClass(displayType)}`}>
                                                    {formatTicketType(displayType)}
                                                </span>
                                            </td>
                                            <td style={{ width: '16%' }} className="message-cell">
                                                <span className="message-text">{displayDesc}</span>
                                            </td>
                                            <td style={{ width: "7%" }}>
                                                <span className={`badge-status ${getStatusBadgeClass(ticket.status)}`}>
                                                    {ticket.status || "UNKNOWN"}
                                                </span>
                                            </td>
                                            <td style={{ width: '8%' }}>
                                                {ticket.createdAt
                                                    ? new Date(ticket.createdAt).toLocaleDateString("en-GB").replace(/\//g, "-")
                                                    : "N/A"}
                                            </td>
                                            <td style={{ width: '7%' }}>
                                                {ticket.createdAt
                                                    ? new Date(ticket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                    : "N/A"}
                                            </td>
                                            <td style={{ width: '8%' }}>
                                                {ticket.resolvedAt ? new Date(ticket.resolvedAt).toLocaleDateString() : '—'}
                                            </td>
                                            <td style={{ width: '12%' }}>
                                                <span className="admin-response-text" title={combinedResponse.response || "No response"}>
                                                    {combinedResponse.response || "No Response"}
                                                  
                                                </span>
                                            </td>
                                            <td style={{ width: '6%' }}>
                                                {ticket.attachmentUrl ? (
                                                    <div className="attachment-thumb">
                                                        <img
                                                            src={ticket.attachmentUrl}
                                                            alt="Attachment"
                                                            onClick={() => showImagePreview(ticket.attachmentUrl)}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = 'https://via.placeholder.com/60x45?text=No+Image';
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <span className="no-image">—</span>
                                                )}
                                            </td>
                                            <td style={{ width: '6%' }}>
                                                <button
                                                    className="btn-view-ticket"
                                                    onClick={() => handleShowViewModal(ticket)}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="12" className="empty-cell">
                                        <div className="empty-state">
                                            <span className="empty-icon">📭</span>
                                            <h5>No tickets found</h5>
                                            <p>{tickets.length === 0 ? "No support tickets have been created yet." : "No tickets match your filters."}</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Image Preview Modal */}
            {imagePreviewModal.show && (
                <div className="image-preview-overlay" onClick={closeImagePreview}>
                    <div className="image-preview-content" onClick={(e) => e.stopPropagation()}>
                        <button className="preview-close" onClick={closeImagePreview}>✕</button>
                        <img src={imagePreviewModal.url} alt="Full size preview" />
                        <div className="preview-actions">
                            <a href={imagePreviewModal.url} target="_blank" rel="noopener noreferrer" className="preview-btn preview-btn-primary">
                                Open in New Tab
                            </a>
                            <a href={imagePreviewModal.url} download className="preview-btn preview-btn-secondary">
                                Download
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* View/Edit Modal */}
            {showViewModal && (
                <div className="view-modal-overlay" onClick={handleCloseViewModal}>
                    <div className="view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="view-modal-header">
                            <h5>
                                <span className="modal-header-icon">🎫</span>
                                {viewLoading ? "Loading Ticket..." : `Ticket #${currentIssueForView?.ticketId || currentIssueForView?.id || "N/A"}`}
                            </h5>
                            <button className="modal-close-btn" onClick={handleCloseViewModal}>✕</button>
                        </div>

                        <div className="view-modal-body">
                            {viewLoading ? (
                                <div className="modal-loading">
                                    <div className="modal-spinner"></div>
                                    <p>Fetching ticket details...</p>
                                </div>
                            ) : currentIssueForView ? (
                                <>
                                    <div className="ticket-info-grid">
                                        <div className="info-item">
                                            <span className="info-label">Ticket ID</span>
                                            <span className="info-value">{currentIssueForView.ticketId || currentIssueForView.id}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Type</span>
                                            <span className={`badge-type ${getTicketTypeBadgeClass(currentIssueForView.issueType || currentIssueForView.ticketType)}`}>
                                                {formatTicketType(currentIssueForView.issueType || currentIssueForView.ticketType)}
                                            </span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">User ID</span>
                                            <span className="info-value">
                                                {currentIssueForView.userId ? (
                                                    <Link to={`/user-profile/${currentIssueForView.userId}`} className="link-user">
                                                        {currentIssueForView.userId}
                                                    </Link>
                                                ) : "N/A"}
                                            </span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Order ID</span>
                                            <span className="info-value">
                                                {currentIssueForView.orderId ? (
                                                    <Link to={`/order-details/${currentIssueForView.orderId}`} className="link-order">
                                                        {currentIssueForView.orderId}
                                                    </Link>
                                                ) : "N/A"}
                                            </span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Created</span>
                                            <span className="info-value">
                                                {currentIssueForView.createdAt ? new Date(currentIssueForView.createdAt).toLocaleString() : 'N/A'}
                                            </span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Resolved</span>
                                            <span className="info-value">
                                                {currentIssueForView.resolvedAt ? new Date(currentIssueForView.resolvedAt).toLocaleString() : 'Not resolved'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="description-section">
                                        <h6>Issue Description</h6>
                                        <div className="description-content">
                                            {currentIssueForView.description || currentIssueForView.message || "No description provided"}
                                        </div>
                                    </div>

                                    <div className="response-section">
                                        <h6>Current Admin Response</h6>
                                        <div className="response-content">
                                            {(() => {
                                                const id = currentIssueForView.ticketId || currentIssueForView.id;
                                                const localResp = localAdminResponses[id];
                                                const displayResp = localResp?.response || currentIssueForView.adminResponse;
                                                return displayResp ? (
                                                    <div className="admin-response-display">
                                                        <span className="admin-response-text">{displayResp}</span>
                                                    </div>
                                                ) : (
                                                    <span className="no-response">No response yet</span>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    {currentIssueForView.attachmentUrl && (
                                        <div className="attachment-section">
                                            <h6>Attachment</h6>
                                            <div className="attachment-preview">
                                                <img
                                                    src={currentIssueForView.attachmentUrl}
                                                    alt="Ticket attachment"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://via.placeholder.com/200x120?text=Image';
                                                    }}
                                                />
                                                <div className="attachment-actions">
                                                    <a href={currentIssueForView.attachmentUrl} target="_blank" rel="noopener noreferrer" className="preview-btn preview-btn-primary">
                                                        Open
                                                    </a>
                                                    <a href={currentIssueForView.attachmentUrl} download className="preview-btn preview-btn-secondary">
                                                        Download
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="update-controls">
                                        <div className="update-group">
                                            <h6>Update Status</h6>
                                            <select
                                                className="update-select"
                                                value={newStatus}
                                                onChange={(e) => setNewStatus(e.target.value)}
                                            >
                                                <option value="">— Select status —</option>
                                                {STATUS_OPTIONS.map(s => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                            <div className="current-status">
                                                Current: <span className={`badge-status ${getStatusBadgeClass(currentIssueForView.status)}`}>
                                                    {currentIssueForView.status}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="update-group">
                                            <h6>Admin Response</h6>
                                            <textarea
                                                className="update-textarea"
                                                placeholder="Type your admin response here..."
                                                value={adminResponse}
                                                onChange={(e) => setAdminResponse(e.target.value)}
                                                rows="4"
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="modal-error">
                                    <span className="error-icon">⚠️</span>
                                    <p>Failed to load ticket details</p>
                                </div>
                            )}
                        </div>

                        <div className="view-modal-footer">
                            <button className="btn-modal-close" onClick={handleCloseViewModal} disabled={saving}>
                                Close
                            </button>
                            <button className="btn-modal-save" onClick={handleSaveChanges} disabled={(!newStatus && !adminResponse) || saving}>
                                {saving ? (
                                    <>
                                        <span className="btn-spinner"></span>
                                        Saving...
                                    </>
                                ) : (
                                    "Save Changes"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Usercoustomercare;